import React, { useState, useEffect, useMemo } from 'react';
import { api, loadBuildingsForSelector } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import { feeCategoriesApi, FeeCategory } from '../../api/feeCategoriesApi';
import { apartmentsApi, Apartment, ApartmentGroup } from '../../api/apartmentsApi';
import ApartmentGroupMultiSelect from '../../components/apartments/ApartmentGroupMultiSelect';
import ApartmentMultiSelect from '../../components/apartments/ApartmentMultiSelect';
import {
  isBulkCreatableFeeCode,
  isFeeCategoryGroupParent,
} from '../../utils/feeCategoryCodeGrouping';
import { getFeeCategoryBillingLabel, isMeterBasedFeeCategory } from '../../utils/feeCategoryBillingKind';
import { isCardIssuanceFeeCode } from '../../utils/accessCardFee';
import {
  describeNoticePeriodSummary,
  resolveCategoryInvoicePeriod,
} from '../../utils/feeBillingTiming';
import {
  serviceFeeManagementApi,
  BulkCreationRecord,
} from '../../api/serviceFeeManagementApi';
import BillingPeriodMonthPicker from '../../components/common/BillingPeriodMonthPicker';
import { getCurrentBillingPeriodValue } from '../../utils/billingPeriodPicker';
import toast from 'react-hot-toast';
import {
  bulkCreationActionBadgeClass,
  bulkCreationActionLabel,
  buildBulkCreationDetailRows,
  BulkCreationActionFilter,
  normalizeBulkCreationDetails,
} from '../../utils/bulkCreationHistoryDetails';

interface Building {
  id: string;
  name: string;
}

export const CreateFeesTab: React.FC = () => {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingId, setBuildingId] = useState('');
  const [billingPeriod, setBillingPeriod] = useState(getCurrentBillingPeriodValue());
  const [categories, setCategories] = useState<FeeCategory[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedApartmentIds, setSelectedApartmentIds] = useState<string[]>([]);
  const [apartmentGroups, setApartmentGroups] = useState<ApartmentGroup[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [overwrite, setOverwrite] = useState(true);
  const [includeZeroAmount, setIncludeZeroAmount] = useState(false);
  const [creating, setCreating] = useState(false);
  const [history, setHistory] = useState<BulkCreationRecord[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [detailRecord, setDetailRecord] = useState<BulkCreationRecord | null>(null);

  const [historyBuildingId, setHistoryBuildingId] = useState('');
  const [historyBillingPeriod, setHistoryBillingPeriod] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const historyLimit = 20;

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      const defaultId = applyDefaultBuildingSelection(list, user, '');
      if (defaultId) {
        setBuildingId((prev) => prev || defaultId);
        setHistoryBuildingId((prev) => prev || defaultId);
      }
    }).catch(console.error);
  }, [user]);

  useEffect(() => {
    if (buildingId) {
      loadCategories();
      loadApartmentData();
    } else {
      setApartmentGroups([]);
      setApartments([]);
      setSelectedGroupIds([]);
      setSelectedApartmentIds([]);
    }
  }, [buildingId]);

  useEffect(() => {
    setSelectedApartmentIds([]);
  }, [selectedGroupIds, buildingId]);

  const buildingApartmentGroups = useMemo(
    () =>
      apartmentGroups.filter(
        (group) => group.buildingId === buildingId && group.status === 'ACTIVE',
      ),
    [apartmentGroups, buildingId],
  );

  useEffect(() => {
    setHistoryPage(1);
  }, [historyBuildingId, historyBillingPeriod]);

  useEffect(() => {
    if (historyBuildingId) {
      loadHistory(historyPage);
    } else {
      setHistory([]);
      setHistoryTotal(0);
    }
  }, [historyBuildingId, historyBillingPeriod, historyPage]);

  const loadApartmentData = async () => {
    try {
      const [groups, apartmentList] = await Promise.all([
        apartmentsApi.getApartmentGroups(),
        apartmentsApi.getApartments({ buildingId }),
      ]);
      setApartmentGroups(groups);
      setApartments(apartmentList.filter((apt) => apt.status === 'ACTIVE'));
      setSelectedGroupIds([]);
      setSelectedApartmentIds([]);
    } catch {
      toast.error('Không thể tải nhóm/căn hộ');
    }
  };

  const loadCategories = async () => {
    try {
      const data = await feeCategoriesApi.getAll(buildingId);
      const residentCategories = (data || []).filter(
        (c) =>
          c.isActive &&
          c.feeCode &&
          !isFeeCategoryGroupParent(c.feeCode) &&
          !isCardIssuanceFeeCode(c.feeCode) &&
          isBulkCreatableFeeCode(c.feeCode) &&
          (c.autoCloseMonthly || isMeterBasedFeeCategory(c)),
      );
      setCategories(residentCategories);
      setSelectedCategoryIds([]);
    } catch {
      toast.error('Không thể tải danh mục phí');
    }
  };

  const loadHistory = async (page = historyPage) => {
    try {
      const data = await serviceFeeManagementApi.getBulkCreateHistory(
        historyBuildingId,
        page,
        historyLimit,
        historyBillingPeriod || undefined,
      );
      setHistory(data.items);
      setHistoryTotal(data.total);
      if (data.page !== page) {
        setHistoryPage(data.page);
      }
    } catch {
      console.error('Failed to load history');
    }
  };

  const historyTotalPages = Math.max(1, Math.ceil(historyTotal / historyLimit));

  const toggleCategory = (id: string) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const selectAll = () => {
    if (selectedCategoryIds.length === categories.length) {
      setSelectedCategoryIds([]);
    } else {
      setSelectedCategoryIds(categories.map((c) => c.id));
    }
  };

  const handleBulkCreate = async () => {
    if (!buildingId) {
      toast.error('Vui lòng chọn chung cư');
      return;
    }
    if (selectedCategoryIds.length === 0) {
      toast.error('Vui lòng chọn ít nhất một loại phí');
      return;
    }

    setCreating(true);
    try {
      const result = await serviceFeeManagementApi.bulkCreate({
        buildingId,
        billingPeriod,
        feeCategoryIds: selectedCategoryIds,
        overwrite,
        includeZeroAmount,
        apartmentGroupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
        apartmentIds: selectedApartmentIds.length > 0 ? selectedApartmentIds : undefined,
      });
      toast.success(
        `Tạo phí thành công: ${result.summary?.createdCount || result.createdCount} mới, ` +
        `${result.summary?.updatedCount || result.updatedCount} cập nhật, ` +
        `${result.summary?.skippedCount || result.skippedCount} bỏ qua`,
      );
      setShowModal(false);
      setDetailRecord(result);
      setSelectedGroupIds([]);
      setSelectedApartmentIds([]);
      if (historyBuildingId === buildingId) {
        setHistoryPage(1);
        loadHistory(1);
      } else {
        setHistoryBuildingId(buildingId);
        setHistoryBillingPeriod('');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Lỗi khi tạo phí dịch vụ');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('vi-VN');

  const handleRevert = async (record: BulkCreationRecord) => {
    const categoryLabel =
      (record.feeCategoryNames || []).length > 0
        ? record.feeCategoryNames!.join(', ')
        : 'các loại phí đã chọn';
    const confirmed = window.confirm(
      `Xóa phí dịch vụ đã tạo cho kỳ ${record.billingPeriod} (${categoryLabel})?\n\n` +
        'Chỉ xóa hóa đơn chưa thanh toán. Sau khi xóa bạn có thể tạo phí lại.',
    );
    if (!confirmed) return;

    setDeletingId(record.id);
    try {
      const result = await serviceFeeManagementApi.revertBulkCreation(record.id);
      toast.success(
        result.deletedCount > 0
          ? `Đã xóa ${result.deletedCount} hóa đơn` +
              (result.skippedPaidCount > 0 ? ` (giữ ${result.skippedPaidCount} đã thanh toán)` : '')
          : result.skippedPaidCount > 0
            ? `Không có hóa đơn chưa thanh toán. Giữ ${result.skippedPaidCount} hóa đơn đã thanh toán.`
            : 'Đã đánh dấu xóa lịch sử tạo phí',
      );
      loadHistory(historyPage);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Không thể xóa phí dịch vụ');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600 text-sm">
          Tạo phí dịch vụ hàng loạt theo <strong>kỳ thu phí</strong>. Chỉ áp dụng phí{' '}
          <strong>Tự động</strong> (quản lý, gửi xe, …). Phí điện/nước tạo hóa đơn kỳ trước theo chỉ số.
          Phí phát hành thẻ (Thủ công) không tạo qua đây — thêm từng hóa đơn trên căn hộ khi cấp thẻ.
        </p>
        <button
          onClick={() => setShowModal(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-lg font-medium whitespace-nowrap"
        >
          + Tạo phí dịch vụ
        </button>
      </div>

      <h3 className="text-lg font-semibold text-gray-900 mb-3">Lịch sử tạo phí</h3>

      <div className="flex flex-wrap gap-4 mb-4">
        {shouldShowBuildingSelector(buildings) && (
        <div className="min-w-[200px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Chung cư</label>
          <select
            value={historyBuildingId}
            onChange={(e) => setHistoryBuildingId(e.target.value)}
                disabled={!needsBuildingSelection(buildings)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Chọn chung cư</option>
            {buildings.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        )}
        <div className="min-w-[160px]">
          <label className="block text-sm font-medium text-gray-700 mb-1">Kỳ tính phí</label>
          <BillingPeriodMonthPicker
            value={historyBillingPeriod}
            onChange={setHistoryBillingPeriod}
            disabled={!historyBuildingId}
            allowEmpty
            emptyLabel="Tất cả kỳ"
          />
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Thời gian</th>
              <th className="px-4 py-3 text-left font-semibold">Kỳ tính phí</th>
              <th className="px-4 py-3 text-left font-semibold">Danh mục phí</th>
              <th className="px-4 py-3 text-left font-semibold">Nhóm căn hộ</th>
              <th className="px-4 py-3 text-left font-semibold">Căn hộ</th>
              <th className="px-4 py-3 text-center font-semibold">Số phí được tạo</th>
              <th className="px-4 py-3 text-left font-semibold">Người tạo</th>
              <th className="px-4 py-3 text-center font-semibold">Tạo mới</th>
              <th className="px-4 py-3 text-center font-semibold">Cập nhật</th>
              <th className="px-4 py-3 text-center font-semibold">Bỏ qua</th>
              <th className="px-4 py-3 text-center font-semibold">Lỗi</th>
              <th className="px-4 py-3 text-center font-semibold">Ghi đè</th>
              <th className="px-4 py-3 text-center font-semibold">Chi tiết</th>
              <th className="px-4 py-3 text-center font-semibold">Xóa</th>
            </tr>
          </thead>
          <tbody>
            {history.length === 0 ? (
              <tr>
                <td colSpan={14} className="px-4 py-8 text-center text-gray-500">
                  {historyBuildingId ? 'Chưa có lịch sử tạo phí' : 'Chọn chung cư để xem lịch sử'}
                </td>
              </tr>
            ) : (
              history.map((record) => (
                <tr key={record.id} className="border-t border-gray-100">
                  <td className="px-4 py-2">{formatDate(record.createdAt)}</td>
                  <td className="px-4 py-2">{record.billingPeriod}</td>
                  <td className="px-4 py-2">
                    {(record.feeCategoryNames || []).length > 0
                      ? record.feeCategoryNames!.join(', ')
                      : '-'}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {record.groupLabel || 'Tất cả'}
                  </td>
                  <td className="px-4 py-2 text-gray-700 max-w-[180px]">
                    {record.apartmentLabel || 'Tất cả'}
                  </td>
                  <td className="px-4 py-2 text-center font-medium text-gray-900">
                    {record.feesCreatedCount ?? record.createdCount + record.updatedCount}
                  </td>
                  <td className="px-4 py-2">{record.creator?.name || '-'}</td>
                  <td className="px-4 py-2 text-center text-green-600">{record.createdCount}</td>
                  <td className="px-4 py-2 text-center text-blue-600">{record.updatedCount}</td>
                  <td className="px-4 py-2 text-center text-gray-500">{record.skippedCount}</td>
                  <td className="px-4 py-2 text-center text-red-600">{record.errorCount}</td>
                  <td className="px-4 py-2 text-center">{record.overwrite ? 'Có' : 'Không'}</td>
                  <td className="px-4 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => setDetailRecord(record)}
                      className="text-cyan-700 hover:text-cyan-900 text-sm font-medium underline"
                    >
                      Xem
                    </button>
                  </td>
                  <td className="px-4 py-2 text-center">
                    {record.revertedAt ? (
                      <span
                        className="text-gray-500 text-xs"
                        title={`${record.reverter?.name || '—'} xóa ${record.revertDeletedCount ?? 0} hóa đơn lúc ${formatDate(record.revertedAt)}`}
                      >
                        Đã xóa
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRevert(record)}
                        disabled={deletingId === record.id}
                        className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                      >
                        {deletingId === record.id ? 'Đang xóa...' : 'Xóa'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {historyBuildingId && historyTotal > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-sm text-gray-600">
          <span>
            {historyTotal} lần tạo phí
            {historyBillingPeriod ? ` · kỳ ${historyBillingPeriod}` : ''}
            {' · '}
            trang {historyPage}/{historyTotalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={historyPage <= 1}
              onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
            >
              Trước
            </button>
            <button
              type="button"
              disabled={historyPage >= historyTotalPages}
              onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
              className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
            >
              Sau
            </button>
          </div>
        </div>
      )}

      {detailRecord && (
        <BulkCreationDetailModal
          record={detailRecord}
          formatDate={formatDate}
          onClose={() => setDetailRecord(null)}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Tạo phí dịch vụ</h2>

              <div className="space-y-4">
                {shouldShowBuildingSelector(buildings) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chung cư</label>
                  <select
                    value={buildingId}
                    onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Chọn chung cư</option>
                    {buildings.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>
                </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kỳ thu phí</label>
                  <BillingPeriodMonthPicker
                    value={billingPeriod}
                    onChange={setBillingPeriod}
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">{describeNoticePeriodSummary(billingPeriod)}</p>
                </div>

                {buildingId && (
                  <ApartmentGroupMultiSelect
                    groups={buildingApartmentGroups}
                    selectedGroupIds={selectedGroupIds}
                    onChange={setSelectedGroupIds}
                    helperText="Tùy chọn. Bỏ trống để áp dụng tất cả căn hộ trong chung cư."
                  />
                )}

                {buildingId && (
                  <ApartmentMultiSelect
                    apartments={apartments}
                    selectedApartmentIds={selectedApartmentIds}
                    selectedGroupIds={selectedGroupIds}
                    onChange={setSelectedApartmentIds}
                  />
                )}

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-700">Danh mục phí</label>
                    <button
                      type="button"
                      onClick={selectAll}
                      className="text-sm text-cyan-600 hover:underline"
                    >
                      {selectedCategoryIds.length === categories.length ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
                    </button>
                  </div>
                  <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                    {categories.map((cat) => (
                      <label key={cat.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedCategoryIds.includes(cat.id)}
                          onChange={() => toggleCategory(cat.id)}
                          className="rounded border-gray-300 text-cyan-600"
                        />
                        <span className="text-sm flex-1">{cat.name}</span>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          HĐ {resolveCategoryInvoicePeriod(billingPeriod, cat)}
                        </span>
                        <span className="text-xs text-gray-400 ml-1">
                          {getFeeCategoryBillingLabel(cat)}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={overwrite}
                    onChange={(e) => setOverwrite(e.target.checked)}
                    className="rounded border-gray-300 text-cyan-600"
                  />
                  <span className="text-sm text-gray-700">
                    Ghi đè phí đã tồn tại (trừ phí đã thanh toán)
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeZeroAmount}
                    onChange={(e) => setIncludeZeroAmount(e.target.checked)}
                    className="rounded border-gray-300 text-cyan-600"
                  />
                  <span className="text-sm text-gray-700">
                    Bao gồm phí 0 đồng
                  </span>
                </label>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  onClick={handleBulkCreate}
                  disabled={creating}
                  className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg disabled:opacity-50"
                >
                  {creating ? 'Đang tạo...' : 'Tạo phí'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BULK_DETAIL_PAGE_SIZE = 50;

const bulkDetailFilterButtonClass = (active: boolean) =>
  `px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${
    active
      ? 'bg-cyan-700 text-white border-cyan-700'
      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
  }`;

const BulkCreationDetailModal: React.FC<{
  record: BulkCreationRecord;
  formatDate: (dateStr: string) => string;
  onClose: () => void;
}> = ({ record, formatDate, onClose }) => {
  const { warnings, categorySummaries } = normalizeBulkCreationDetails(record.details);
  const allRows = useMemo(
    () => buildBulkCreationDetailRows(record.details),
    [record.details],
  );
  const [actionFilter, setActionFilter] = useState<BulkCreationActionFilter>('all');
  const [page, setPage] = useState(1);

  const filteredRows = useMemo(() => {
    if (actionFilter === 'all') return allRows;
    return allRows.filter((row) => row.action === actionFilter);
  }, [allRows, actionFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / BULK_DETAIL_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = filteredRows.slice(
    (safePage - 1) * BULK_DETAIL_PAGE_SIZE,
    safePage * BULK_DETAIL_PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [actionFilter, record.id]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const categoryLabel =
    (record.feeCategoryNames || []).length > 0
      ? record.feeCategoryNames!.join(', ')
      : '—';

  const actionCounts = useMemo(() => {
    const counts = { created: 0, updated: 0, skipped: 0, error: 0 };
    for (const row of allRows) {
      if (row.action in counts) {
        counts[row.action as keyof typeof counts] += 1;
      }
    }
    return counts;
  }, [allRows]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-gray-200 flex justify-between items-start gap-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Chi tiết lần Tạo phí</h2>
            <p className="text-sm text-gray-600 mt-1">
              {formatDate(record.createdAt)} · Kỳ <strong>{record.billingPeriod}</strong> ·{' '}
              {record.apartmentLabel || 'Tất cả căn'} · Ghi đè:{' '}
              <strong>{record.overwrite ? 'Có' : 'Không'}</strong>
            </p>
            <p className="text-sm text-gray-600 mt-0.5">{categoryLabel}</p>
            <p className="text-sm mt-2 flex flex-wrap items-center gap-2">
              <span className="text-gray-600">Tổng kết lần chạy:</span>
              <span className="text-green-700 font-medium">{record.createdCount} tạo mới</span>
              <span className="text-gray-400">·</span>
              <span className="text-blue-700 font-medium">{record.updatedCount} cập nhật</span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-600">{record.skippedCount} bỏ qua</span>
              <span className="text-gray-400">·</span>
              <span className="text-red-700">{record.errorCount} lỗi</span>
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <span className="text-xs text-gray-500">Lọc chi tiết:</span>
              <button
                type="button"
                className={bulkDetailFilterButtonClass(actionFilter === 'all')}
                onClick={() => setActionFilter('all')}
              >
                Tất cả ({allRows.length})
              </button>
              <button
                type="button"
                className={bulkDetailFilterButtonClass(actionFilter === 'created')}
                onClick={() => setActionFilter('created')}
              >
                Tạo mới ({actionCounts.created})
              </button>
              <button
                type="button"
                className={bulkDetailFilterButtonClass(actionFilter === 'updated')}
                onClick={() => setActionFilter('updated')}
              >
                Cập nhật ({actionCounts.updated})
              </button>
              <button
                type="button"
                className={bulkDetailFilterButtonClass(actionFilter === 'skipped')}
                onClick={() => setActionFilter('skipped')}
              >
                Bỏ qua ({actionCounts.skipped})
              </button>
              {actionCounts.error > 0 && (
                <button
                  type="button"
                  className={bulkDetailFilterButtonClass(actionFilter === 'error')}
                  onClick={() => setActionFilter('error')}
                >
                  Lỗi ({actionCounts.error})
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-2xl leading-none px-2"
            aria-label="Đóng"
          >
            ×
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {categorySummaries.length > 0 && (
            <div className="mb-4 rounded-lg border border-gray-200 overflow-hidden">
              <p className="text-sm font-medium text-gray-800 bg-gray-50 px-4 py-2 border-b border-gray-200">
                Tổng hợp theo danh mục phí
              </p>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Danh mục</th>
                    <th className="px-3 py-2 text-left font-semibold">Mã</th>
                    <th className="px-3 py-2 text-right font-semibold">Tạo mới</th>
                    <th className="px-3 py-2 text-right font-semibold">Cập nhật</th>
                    <th className="px-3 py-2 text-right font-semibold">Bỏ qua</th>
                  </tr>
                </thead>
                <tbody>
                  {categorySummaries.map((row) => (
                    <tr key={`${row.feeCode}-${row.feeCategory}`} className="border-t border-gray-100">
                      <td className="px-3 py-2 text-gray-800">{row.feeCategory}</td>
                      <td className="px-3 py-2 font-mono text-gray-600">{row.feeCode || '—'}</td>
                      <td className="px-3 py-2 text-right text-green-700 font-medium">
                        {row.createdCount}
                      </td>
                      <td className="px-3 py-2 text-right text-blue-700">{row.updatedCount}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{row.skippedCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-gray-500 px-4 py-2 border-t border-gray-100">
                Mỗi căn chỉ nhận một mã PQL theo loại căn hộ; điện/nước chỉ tạo khi đã có chỉ số kỳ.
                Căn <strong>Bỏ qua</strong> (thiếu diện tích, loại căn không khớp PQL, …) xem bộ lọc Bỏ qua
                hoặc bảng chi tiết — danh sách đầy đủ được lưu kể từ lần Tạo phí sau cập nhật hệ thống.
              </p>
            </div>
          )}
          {warnings.length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-medium mb-1">Cảnh báo</p>
              <ul className="list-disc list-inside space-y-0.5">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          {allRows.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">
              Không có dòng chi tiết theo căn hộ (chỉ có tổng số trên bảng). Lần chạy có thể chỉ
              cập nhật tổng hợp hoặc dữ liệu cũ trước khi lưu chi tiết.
            </p>
          ) : filteredRows.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">
              Không có dòng nào khớp bộ lọc đã chọn.
            </p>
          ) : (
            <>
              <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Căn hộ</th>
                    <th className="px-3 py-2 text-left font-semibold">Danh mục phí</th>
                    <th className="px-3 py-2 text-left font-semibold">Kết quả</th>
                    <th className="px-3 py-2 text-left font-semibold">Ghi chú</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, index) => (
                    <tr
                      key={`${row.apartmentCode}-${row.feeCategory}-${row.action}-${safePage}-${index}`}
                      className="border-t border-gray-100"
                    >
                      <td className="px-3 py-2 font-medium text-gray-900">{row.apartmentCode}</td>
                      <td className="px-3 py-2 text-gray-700">{row.feeCategory}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${bulkCreationActionBadgeClass(row.action)}`}
                        >
                          {bulkCreationActionLabel(row.action)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{row.message || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex flex-wrap items-center justify-between gap-3 mt-3 text-sm text-gray-600">
                <span>
                  Hiển thị {(safePage - 1) * BULK_DETAIL_PAGE_SIZE + 1}–
                  {Math.min(safePage * BULK_DETAIL_PAGE_SIZE, filteredRows.length)} /{' '}
                  {filteredRows.length} dòng
                  {actionFilter !== 'all' ? ` (đã lọc, tổng ${allRows.length})` : ''}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                  >
                    Trước
                  </button>
                  <span>
                    Trang {safePage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
                  >
                    Sau
                  </button>
                </div>
              </div>
            </>
          )}
          {allRows.length > 0 &&
            actionCounts.created + actionCounts.updated <
              record.createdCount + record.updatedCount && (
              <p className="text-xs text-amber-700 mt-3">
                Một số dòng tạo mới/cập nhật không hiển thị hết (giới hạn ~600 dòng mẫu). Tổng kết và
                bảng theo danh mục phí vẫn đúng; căn bỏ qua luôn liệt kê đầy đủ khi lịch sử có skipLogs.
              </p>
            )}
          {record.skippedCount > 0 && actionCounts.skipped < record.skippedCount && (
            <p className="text-xs text-amber-700 mt-3">
              Lịch sử cũ có thể không lưu từng căn bỏ qua — xem cột Bỏ qua trong tổng hợp theo danh mục,
              hoặc chạy lại Tạo phí để có danh sách căn chi tiết.
            </p>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateFeesTab;
